import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Megaphone, Users, Kanban, Brain, Mail, UserCog, Search, BarChart3, Loader2 } from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { useTools, categories } from '../hooks/useData';

const iconMap = {
  Megaphone: Megaphone,
  Users: Users,
  Kanban: Kanban,
  Brain: Brain,
  Mail: Mail,
  UserCog: UserCog,
  Search: Search,
  BarChart3: BarChart3,
};

const CategoriesPage = () => {
  const { tools, loading } = useTools();

  // Calculate actual tool count for each category
  const getCategoryToolCount = (categorySlug) => {
    return tools.filter(t => t.categorySlug === categorySlug).length;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-12">
            <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-3">
              All Categories
            </h1>
            <p className="text-lg text-gray-600">
              Browse our comprehensive collection of software categories
            </p>
          </div>

          {/* Categories Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
              <span className="ml-2 text-gray-600">Loading categories...</span>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {categories.map((category) => {
                const IconComponent = iconMap[category.icon] || Megaphone;
                const actualToolCount = getCategoryToolCount(category.slug);
                return (
                  <Link
                    key={category.id}
                    to={`/category/${category.slug}`}
                    className="group relative bg-white rounded-2xl border border-gray-100 p-6 hover:border-gray-200 hover:shadow-xl hover:shadow-gray-100/50 transition-all duration-300"
                  >
                    <div
                      className={`w-14 h-14 rounded-xl bg-gradient-to-br ${category.color} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300 shadow-lg`}
                    >
                      <IconComponent className="w-7 h-7 text-white" />
                    </div>

                    <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-emerald-600 transition-colors">
                      {category.name}
                    </h3>
                    <p className="text-sm text-gray-500 mb-4 line-clamp-2">
                      {category.description}
                    </p>

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-400">
                        {actualToolCount} tools
                      </span>
                      <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-emerald-600 group-hover:translate-x-1 transition-all" />
                    </div>

                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-emerald-500/5 to-teal-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CategoriesPage;
